import React from 'react';
import { MoreVertical, Trash2, AlertTriangle } from 'lucide-react';

interface PostHeaderProps {
  username: string;
  avatar?: string;
  userId: string;
  currentUserId: string;
  onDelete?: () => void;
}

export function PostHeader({ username, avatar, userId, currentUserId, onDelete }: PostHeaderProps) {
  const [showOptions, setShowOptions] = React.useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

  return (
    <>
      <div className="relative flex items-center justify-between px-4 py-3 sm:px-5 sm:py-3.5 z-20">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src={avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=random&size=40&color=fff`}
              alt={username}
              className="w-9 h-9 rounded-full ring-2 ring-gray-100 dark:ring-gray-700"
              loading="eager"
              decoding="async"
              fetchpriority="high"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=random&size=40&color=fff`;
              }}
            />
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
          </div>
          <span className="font-semibold text-gray-900 dark:text-white text-sm">
            {username}
          </span>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowOptions(!showOptions)}
            className={`p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${
              userId !== currentUserId ? 'hidden' : ''
            }`}
          >
            <MoreVertical className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </button>

          {showOptions && onDelete && (
            <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-1 animate-fade-in">
              <button
                onClick={() => {
                  setShowOptions(false);
                  setShowDeleteConfirmation(true);
                }}
                className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm border border-gray-200 dark:border-gray-700">
            <div className="p-5 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Confirmer la suppression
                </h3>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Cette action est irreversible. Le post et tous ses commentaires seront supprimes.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteConfirmation(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    onDelete();
                    setShowDeleteConfirmation(false);
                  }}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}